import type { MessagePayload } from "@/db/schema/chat";

/**
 * Live location — WhatsApp's "share live location": a location message whose
 * position the sharer's device keeps updating until it ends (a chosen
 * duration, or "Stop sharing"). Shared by the server actions, the composer
 * sheet, the message card and the app-wide sharer.
 */
export const LIVE_LOCATION_DURATIONS_MINUTES = [15, 60, 480] as const;
export type LiveLocationDurationMinutes =
  (typeof LIVE_LOCATION_DURATIONS_MINUTES)[number];

/** The server ignores updates closer together than this (battery + writes). */
export const LIVE_LOCATION_MIN_UPDATE_INTERVAL_MS = 10_000;

export type LiveLocationPayload = Extract<MessagePayload, { mode: "live" }>;

export function isLiveLocationPayload(
  payload: MessagePayload | null | undefined,
): payload is LiveLocationPayload {
  return (
    !!payload &&
    typeof payload === "object" &&
    "mode" in payload &&
    payload.mode === "live" &&
    typeof payload.lat === "number" &&
    typeof payload.lng === "number" &&
    typeof payload.liveUntil === "number"
  );
}

/** Still moving on the map: not stopped early and not past its end time. */
export function isLiveLocationActive(
  payload: LiveLocationPayload,
  now: number = Date.now(),
) {
  return !payload.endedAt && payload.liveUntil > now;
}

export function formatLiveLocationDuration(minutes: number) {
  if (minutes < 60) return `${minutes} minutes`;
  const hours = minutes / 60;
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

/** Great-circle distance in metres (haversine) — for update throttling. */
export function distanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
) {
  const R = 6_371_000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
